import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import API from '../services/api';


function TherapistMessagesPage() {
  const [messages, setMessages] = useState([]);
  // Change default to `true` so it initially shows only unread messages
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);

  // Whenever the unread toggle changes, refetch messages from the server using
  // the appropriate query param.  This replaces client-side filtering.
  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line
  }, [showUnreadOnly]);

  const fetchMessages = async () => {
    try {
      // Build the URL based on whether we only want unread messages.  When
      // showUnreadOnly is true we request only unread messages server-side,
      // otherwise we request all messages.
      const url = showUnreadOnly ? '/therapist/messages?unreadOnly=true' : '/therapist/messages';
      const res = await API.get(url);
      setMessages(res.data);
    } catch (err) {
      console.error(err);
      alert('Error fetching messages');
    }
  };

  const handleToggleUnreadOnly = () => {
    setShowUnreadOnly(!showUnreadOnly);
  };

  // The messages list is now provided filtered by the server, so we simply display
  // the messages array as-is.
  const displayedMessages = messages;

  // Mark read/unread
  const updateReadStatus = async (messageId, newReadStatus) => {
    try {
      await API.put(`/therapist/messages/${messageId}`, { isRead: newReadStatus });
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId
            ? {
                ...m,
                is_read: newReadStatus,
                read_at: newReadStatus ? new Date().toISOString() : null
              }
            : m
        )
      );
    } catch (err) {
      console.error('Error updating read status:', err);
      alert('Error updating read status');
    }
  };

  // Acknowledge a message that requires acknowledgement
  const acknowledgeMessage = async (messageId) => {
    try {
      await API.put(`/therapist/messages/${messageId}`, { acknowledged: true });
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId
            ? {
                ...m,
                acknowledged_at: new Date().toISOString()
              }
            : m
        )
      );
    } catch (err) {
      console.error('Error acknowledging message:', err);
      alert('Error acknowledging message');
    }
  };

  return (
    <div style={{ margin: '2rem' }}>
      <h2>Therapist Messages</h2>

      <label style={{ marginRight: '1rem' }}>
        <input 
          type="checkbox"
          checked={showUnreadOnly}
          onChange={handleToggleUnreadOnly}
        />
        Show Unread Only
      </label>

      {displayedMessages.length === 0 
        ? <p>No messages found.</p>
        : displayedMessages.map(msg => (
            <div
              key={msg.id}
              style={{
                border: '1px solid var(--border-color)',
                padding: '1rem',
                marginBottom: '1rem'
              }}
            >
              <h4>
                {msg.title}{' '}
                {!msg.is_read && <span style={{ color: 'red' }}>(Unread)</span>}
                {/* Show priority label for important or critical messages */}
                {msg.priority && msg.priority > 1 && (
                  <span
                    style={{
                      marginLeft: '0.5rem',
                      color: msg.priority === 3 ? 'var(--error-color)' : '#ea580c',
                      fontWeight: 'bold'
                    }}
                  >
                    {msg.priority === 3 ? 'Critical' : 'Important'}
                  </span>
                )}
              </h4>

              {/* Render the message content as HTML */}
              <p 
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content || '') }}
              />

              {msg.image_url && (
                <img
                  src={msg.image_url}
                  alt="Message"
                  style={{
                    maxWidth: '200px',
                    display: 'block',
                    marginBottom: '0.5rem'
                  }}
                />
              )}

              <small>Posted on {new Date(msg.created_at).toLocaleString()}</small>
              {/* Show acknowledgement status if required */}
              {msg.requires_ack && (
                <div style={{ marginTop: '0.25rem' }}>
                  {msg.acknowledged_at ? (
                    <span style={{ color: '#16a34a' }}>Acknowledged</span>
                  ) : (
                    <span style={{ color: '#f59e0b' }}>Requires acknowledgement</span>
                  )}
                </div>
              )}
              <br/>
              {msg.is_read ? (
                <button onClick={() => updateReadStatus(msg.id, false)}>
                  Mark Unread
                </button>
              ) : (
                <button onClick={() => updateReadStatus(msg.id, true)}>
                  Mark Read
                </button>
              )}
              {/* Acknowledge button for messages that require it */}
              {msg.requires_ack && !msg.acknowledged_at && (
                <button
                  onClick={() => acknowledgeMessage(msg.id)}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Acknowledge
                </button>
              )}
            </div>
          ))
      }
    </div>
  );
}

export default TherapistMessagesPage;
